import React, { useState } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Input,
  Spinner,
  Text,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Tooltip,
  Tab,
  TabList,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  OpenRegular,
  AddCircle24Regular,
  LinkRegular,
  Warning24Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BoondOpportunity } from '@gridwise/shared';
import {
  getOpportunities,
  getOpportunitiesWithoutNextAction,
  getOpportunitiesToFollowUp,
} from '../api/opportunities';
import StatusBadge from '../components/StatusBadge';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: tokens.colorNeutralBackground2,
  },
  toolbar: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingHorizontalXL,
  },
  tableWrapper: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  tabsBar: {
    padding: `0 ${tokens.spacingHorizontalXL}`,
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  amount: { fontVariantNumeric: 'tabular-nums' },
  overdueDate: { color: tokens.colorPaletteRedForeground1 },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  noAction: {
    color: tokens.colorPaletteRedForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  count: {
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    borderRadius: '10px',
    padding: '0 6px',
    fontSize: '11px',
    fontWeight: 600,
  },
});

type TabValue = 'pipe' | 'relancer' | 'sans-action';

export default function PipeCommercial() {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<TabValue>('pipe');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('');

  const pipeQuery = useQuery({
    queryKey: ['opportunities', 'pipe', search, stageFilter],
    queryFn: () => getOpportunities({ search, stage: stageFilter }),
    enabled: activeTab === 'pipe',
  });

  const followUpQuery = useQuery({
    queryKey: ['opportunities', 'follow-up'],
    queryFn: () => getOpportunitiesToFollowUp({}),
    enabled: activeTab === 'relancer',
  });

  const noActionQuery = useQuery({
    queryKey: ['opportunities', 'no-action'],
    queryFn: () => getOpportunitiesWithoutNextAction({}),
    enabled: activeTab === 'sans-action',
  });

  const currentQuery =
    activeTab === 'pipe' ? pipeQuery :
    activeTab === 'relancer' ? followUpQuery :
    noActionQuery;

  const opportunities: BoondOpportunity[] = currentQuery.data?.data || [];

  const formatDate = (d?: string) => {
    if (!d) return '—';
    try {
      return format(new Date(d), 'dd/MM/yyyy', { locale: fr });
    } catch {
      return d;
    }
  };

  const formatAmount = (a?: number) => {
    if (!a) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(a);
  };

  const isOverdue = (d?: string) => {
    if (!d) return false;
    return isBefore(new Date(d), new Date());
  };

  const handleRefresh = () => currentQuery.refetch();

  return (
    <div className={styles.page}>
      <PageHeader
        title="Pipe commercial"
        subtitle="Opportunités & suivi commercial Boond"
        actions={
          <Button
            icon={<ArrowClockwise24Regular />}
            appearance="subtle"
            onClick={handleRefresh}
            disabled={currentQuery.isFetching}
          >
            Actualiser
          </Button>
        }
      />

      <div className={styles.tabsBar}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, d) => setActiveTab(d.value as TabValue)}
        >
          <Tab value="pipe">
            Pipe commercial{' '}
            {pipeQuery.data && (
              <span className={styles.count}>{pipeQuery.data.pagination?.total || pipeQuery.data.data.length}</span>
            )}
          </Tab>
          <Tab value="relancer">
            À relancer{' '}
            {followUpQuery.data && (
              <span className={styles.count}>{followUpQuery.data.data.length}</span>
            )}
          </Tab>
          <Tab value="sans-action">
            Sans prochaine action{' '}
            {noActionQuery.data && (
              <span className={styles.count}>{noActionQuery.data.data.length}</span>
            )}
          </Tab>
        </TabList>
      </div>

      {activeTab === 'pipe' && (
        <div className={styles.toolbar}>
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            style={{ width: 220 }}
          />
          <Dropdown
            placeholder="Stade"
            value={stageFilter}
            onOptionSelect={(_, d) => setStageFilter(d.optionValue || '')}
            style={{ minWidth: 160 }}
          >
            <Option value="">Tous les stades</Option>
            <Option value="1">Qualification</Option>
            <Option value="2">Proposition</Option>
            <Option value="3">Négociation</Option>
            <Option value="4">Gagné</Option>
            <Option value="5">Perdu</Option>
          </Dropdown>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {opportunities.length} opportunité{opportunities.length !== 1 ? 's' : ''}
          </Text>
        </div>
      )}

      <div className={styles.tableContainer}>
        {currentQuery.isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner label="Chargement des opportunités..." />
          </div>
        ) : currentQuery.isError ? (
          <EmptyState
            title="Erreur de chargement"
            description="Impossible de récupérer les données Boond. Vérifiez votre connexion."
          />
        ) : opportunities.length === 0 ? (
          <EmptyState
            title={activeTab === 'sans-action' ? "Toutes les opportunités ont une prochaine action" : "Aucune opportunité"}
            description={activeTab === 'sans-action' ? "Excellent suivi !" : "Modifiez vos filtres pour afficher des résultats."}
          />
        ) : (
          <div className={styles.tableWrapper}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Société</TableHeaderCell>
                  <TableHeaderCell>Opportunité</TableHeaderCell>
                  <TableHeaderCell>Responsable</TableHeaderCell>
                  <TableHeaderCell>Stade</TableHeaderCell>
                  <TableHeaderCell>Montant</TableHeaderCell>
                  <TableHeaderCell>Closing</TableHeaderCell>
                  <TableHeaderCell>Prochaine action</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opp) => (
                  <TableRow key={opp.id}>
                    <TableCell>
                      <TableCellLayout>
                        <Text size={300} weight="semibold">{opp.company?.name || '—'}</Text>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        <Text size={300}>{opp.title}</Text>
                        {opp.reference && (
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                            {' '}#{opp.reference}
                          </Text>
                        )}
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <Text size={300}>{opp.manager || '—'}</Text>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={opp.stage} type="opportunity" />
                    </TableCell>
                    <TableCell>
                      <Text size={300} className={styles.amount}>
                        {formatAmount(opp.amount)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text
                        size={300}
                        className={isOverdue(opp.closingDate) ? styles.overdueDate : ''}
                      >
                        {formatDate(opp.closingDate)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      {!opp.nextActionTitle && !opp.nextActionDate ? (
                        <div className={styles.noAction}>
                          <Warning24Regular fontSize={16} />
                          <Text size={200}>À planifier</Text>
                        </div>
                      ) : (
                        <div>
                          <Text size={300}>{opp.nextActionTitle || '—'}</Text>
                          <br />
                          <Text
                            size={200}
                            className={isOverdue(opp.nextActionDate) ? styles.overdueDate : ''}
                            style={{ color: isOverdue(opp.nextActionDate) ? undefined : tokens.colorNeutralForeground3 }}
                          >
                            {formatDate(opp.nextActionDate)}
                          </Text>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className={styles.actionsCell}>
                        <Tooltip content="Ouvrir dans Boond" relationship="label">
                          <Button
                            icon={<OpenRegular />}
                            appearance="subtle"
                            size="small"
                            as="a"
                            href={`${import.meta.env.VITE_BOOND_APP_URL || '#'}/opportunity/${opp.id}`}
                            target="_blank"
                          />
                        </Tooltip>
                        <Tooltip content="Créer une action" relationship="label">
                          <Button
                            icon={<AddCircle24Regular />}
                            appearance="subtle"
                            size="small"
                            onClick={() => {
                              window.open(
                                `/task/envoyer-vers-boond?entityType=opportunity&entityId=${opp.id}&entityLabel=${encodeURIComponent(opp.title)}`,
                                '_blank',
                                'width=600,height=700',
                              );
                            }}
                          />
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
